import { client } from "@handoverpro/shared/api-client/client";

import { getIdToken } from "~/lib/auth-token";

const BASE_URL = import.meta.env.VITE_API_URL;
if (!BASE_URL) {
  throw new Error("VITE_API_URL is not set");
}

// Thrown on 423 Locked — e.g. a session another clinician has open.
export class ApiLockedError extends Error {
  readonly status = 423 as const;
  readonly response: Response;
  constructor(response: Response) {
    super(`Locked: ${response.url}`);
    this.name = "ApiLockedError";
    this.response = response;
  }
}

// Thrown on 410 Gone — e.g. an expired join code.
export class ApiExpiredError extends Error {
  readonly status = 410 as const;
  readonly response: Response;
  constructor(response: Response) {
    super(`Expired: ${response.url}`);
    this.name = "ApiExpiredError";
    this.response = response;
  }
}

client.setConfig({
  baseUrl: BASE_URL,
});

// The token getter is registered by the AuthProvider (see lib/auth-token.ts),
// keeping this module free of any direct Firebase dependency. The Firebase SDK
// behind it transparently refreshes ID tokens (~1h TTL) and caches in-memory,
// so fetching one per request is cheap.
client.interceptors.request.use(async (request) => {
  const token = await getIdToken();
  if (token) {
    request.headers.set("Authorization", `Bearer ${token}`);
  }
  return request;
});

client.interceptors.response.use(async (response) => {
  if (response.status === 401) {
    // `POST /sessions/validate-code` is the one endpoint that legitimately
    // answers 401 for an in-page UX state (wrong code) rather than an auth
    // failure. The problem+json body carries `reason: "wrong"` only on that
    // path, so we use it — not the URL — to decide whether to redirect. Any
    // other 401 (expired ID token, missing Authorization) keeps the original
    // hard-navigate to /login so React state and in-flight requests tear down.
    const body = (await response
      .clone()
      .json()
      .catch(() => null)) as { reason?: unknown } | null;
    if (body?.reason !== "wrong") {
      window.location.assign("/login");
    }
    return response;
  }
  if (response.status === 423) {
    throw new ApiLockedError(response);
  }
  if (response.status === 410) {
    throw new ApiExpiredError(response);
  }
  return response;
});

export { client };
export * from "@handoverpro/shared/api-client";
